import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
/* jscs:disable maximumLineLength */
import styled, { css } from 'styled-components';
import {
  hasOpenHoursToday,
  hasOpenHoursTomorrow,
} from '../../../../../../../../core/util/location';
import { compactor } from '../../util';
import { hasSlotsAvailable } from '../../../../../../../../core/location/slots';
import {
  TODAY,
  TOMORROW,
  DayLabelSRP,
  ClosedSlotSRP,
  NoAvailableSRP,
  SlotSRP,
} from '~/components/Search/components/Card/components/TimeSlots/components';

const SlotContainer = styled.div.withConfig({ componentId: 'sc-15x7ios-0' })<{ showall?: boolean }>`
  max-width: 560px;
  ${(props) =>
    props.showall &&
    css`
      flex-wrap: wrap;
    `};
  display: flex;
`;

const Slots = styled.div.withConfig({ componentId: 'sc-15x7ios-1' })`
  display: flex;
  flex-wrap: wrap;
`;

const DayContainer = styled.div.withConfig({ componentId: 'sc-15x7ios-2' })<{ today: boolean }>`
  /* the properties below should only apply to the Tomorrow day container */
  /* the idea is that we give it an initial width of one tile (~83px) so that
     it doesn't wrap underneath when there's room for at least one Tomorrow tile
     on the top row */
  /* then once it's on the top row, flex-grow: 1 allows it to grow to fill the
     rest of the row with as many tomorrow timeslots that will fit */
  ${(props) =>
    !props.today &&
    css`
      flex-basis: 83px;
      flex-grow: 1;
    `}
`;

type Props = {
  label?: string;
  labelIcon?: ReactNode;
  location?: any;
  todaySlots?: any[];
  tomorrowSlots?: any[];
  onSlotSelect?: (...args: any[]) => any;
  isSrp?: boolean;
};

const Day = (props: Props) => {
  const {
    todaySlots,
    tomorrowSlots,
    location,
    location: { timeZone },
    onSlotSelect,
    label,
    labelIcon,
    isSrp,
  } = props;

  const { t } = useTranslation();

  let hasOpenHours = null;
  let slots = null;
  let labelCopy = '';

  if (label === TODAY) {
    hasOpenHours = hasOpenHoursToday;
    slots = compactor(todaySlots);
    labelCopy = t('today.uppercase');
  } else if (label === TOMORROW) {
    hasOpenHours = hasOpenHoursTomorrow;
    slots = compactor(tomorrowSlots);
    labelCopy = t('tomorrow.uppercase');
  } else {
    hasOpenHours = () => true;
    slots = tomorrowSlots;
  }

  const isClosed = !hasOpenHours(location);
  const hasSlots = hasSlotsAvailable(slots);
  const tomorrowClosedOrUnavailable =
    !hasOpenHoursTomorrow(location) || !hasSlotsAvailable(tomorrowSlots);
  let component = null;

  const DISPLAY_COUNT = 4;

  if (isClosed) {
    if ((label === TODAY && tomorrowClosedOrUnavailable) || label === TOMORROW) {
      component = <ClosedSlotSRP />;
    } else {
      return null;
    }
  } else if (!hasSlots) {
    if ((label === TODAY && tomorrowClosedOrUnavailable) || label === TOMORROW) {
      component = <NoAvailableSRP />;
    } else {
      return null;
    }
  } else if (slots) {
    component = (
      <Slots>
        {slots.map(
          (slot, i) =>
            i < DISPLAY_COUNT && ( // only a maximum of DISPLAY_COUNT slots are ever shown in the preview
              <SlotSRP
                isSrp={isSrp}
                key={i}
                onSelect={() => onSlotSelect && onSlotSelect(slot, i)}
                slot={slot}
                timeZone={timeZone}
              />
            )
        )}
      </Slots>
    );
  }

  return (
    <DayContainer today={label === TODAY}>
      <DayLabelSRP>
        {labelIcon}
        {labelCopy ?? label}
      </DayLabelSRP>

      <SlotContainer>{component}</SlotContainer>
    </DayContainer>
  );
};

export default Day;
